import React, { ReactNode, useState } from "react";

//import Autocomplete from "@mui/lab/Autocomplete";
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderOptionState,
} from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { debounce } from "lodash";
import { makeStyles } from "tss-react/mui";

import { isNullOrEmpty } from "../../../utils/stringUtils";

export interface AutocompleteProperties<T> {
  id: string;
  label: string;
  onChange?: (
    event: React.ChangeEvent<{}>,
    value: T | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => void;
  getOptionSelected: ((option: T, value: T) => boolean) | undefined;
  getOptionLabel: ((option: T) => string) | undefined;

  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState
  ) => ReactNode;

  onPatternChange: any;
  defaultValue: T | null | undefined;
  helperText: any;
  error: any;
  minCharacters: number;
  maxCharacters: number;
}

const useStyles = makeStyles()((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
  },
}));

export function AutocompleteBase<T>(props: AutocompleteProperties<T>) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const loading = isLoading;
  const { classes } = useStyles();

  const onPatternChange = debounce(async (value: string) => {
    setRequestFailed(false);
    setIsLoading(true);

    try {
      const response = await props.onPatternChange(value);
      setOptions(response ?? []);
    } catch (error) {
      setOptions([]);
      setRequestFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  const requestFailedMessage = "Unable to load results. Please try again.";

  return (
    <FormControl variant="filled" className={classes.formControl} fullWidth required>
      <Autocomplete
        multiple={false}
        freeSolo={false}
        id={props.id}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        onChange={(
          event: any,
          newValue: any,
          reason: AutocompleteChangeReason,
          details?: AutocompleteChangeDetails<T>
        ) => {
          if (reason === "clear") {
            setOptions([]);
            setRequestFailed(false);
          }
          props.onChange && props.onChange(event, newValue, reason, details);
        }}
        getOptionLabel={props.getOptionLabel}
        options={options}
        loading={loading}
        noOptionsText={requestFailed ? requestFailedMessage : "No options"}
        defaultValue={props.defaultValue}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label}
            variant="filled"
            onChange={(ev) => {
              var targetValue = decodeURIComponent(ev.target.value).trim();

              if (isNullOrEmpty(targetValue)) {
                setRequestFailed(false);
                setOptions([]);
                return;
              }

              if (targetValue.length >= props.minCharacters && targetValue.length <= props.maxCharacters) {
                onPatternChange(targetValue);
              }
            }}
            InputProps={{
              ...params.InputProps,
            }}
            required
            helperText={requestFailed ? requestFailedMessage : props.helperText}
            error={Boolean(props.error) || requestFailed}
          />
        )}
        renderOption={props.renderOption}
      />
    </FormControl>
  );
}
